import { EventModel } from "@bryntum/scheduler";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import { FC, useCallback, useMemo, useState } from "react";
import { IFormProps } from "../../../../Components/CoreLib/library";
import { useOrderLineItemEditorForm } from "./useOrderLineItemEditorForm";
import { OrderLineItemDto } from "../../../../dtos";
import { TimePicker, TimeIntervalPicker, MemoForm } from "../../../../Components/CommonInputs";
import { adjustSecondDateToMatchFirst } from "../../../../util/adjustDates";
import { AffectedDispatchesDialog } from "../../Components/AffectedDispatchesDialog";

export interface IOrderLineItemEditorProps extends IFormProps<EventModel> {
    open: boolean;
    disabled: boolean;
    orderLineItemDto?: OrderLineItemDto;
}

export const OrderLineItemEditor: FC<IOrderLineItemEditorProps> = (props) => {
    const {
        open,
        disabled,
        orderLineItemDto,
    } = props;

    const {
        isFormDirty,
        handleSave,
        handleCancel,
        setFormDispatchBillingMemo,
        setFormDriverMemo,
        setFormYardTime,
        setFormOnsiteTime,
        setFormIntervalTime,
        setTimesChanged,
        formDispatchBillingMemo,
        formDriverMemo,
        formYardTime,
        formOnsiteTime,
        formIntervalTime,
        timesChanged,
        affectedDispatches,
        isGettingAffectedDispatches,
        updateOrderLineItemAndGetAffectedDispatches
    } = useOrderLineItemEditorForm({ ...props });

    const [showAffectedDispatchesDialog, setShowAffectedDispatchesDialog] = useState(false);

    const handleAffectedDispatchesDialogSuccess = useCallback(() => {
        setShowAffectedDispatchesDialog(false);
        handleSave();
    }, [setShowAffectedDispatchesDialog, handleSave]);

    const handleAffectedDispatchesDialogCancel = useCallback(() => {
        setShowAffectedDispatchesDialog(false);
        handleSave();
    }, [setShowAffectedDispatchesDialog, handleSave]);

    const handleSaveClicked = useCallback(() => {
        updateOrderLineItemAndGetAffectedDispatches();
        if (timesChanged) {
            setShowAffectedDispatchesDialog(true);
        }
    }, [timesChanged, setShowAffectedDispatchesDialog, updateOrderLineItemAndGetAffectedDispatches]);

    const memoizedOrderLineItems = useMemo(() => {
        return orderLineItemDto?.lineItemId
            ? [{
                ...orderLineItemDto,
                yardTime: formYardTime ?? undefined,
                onsiteTime: formOnsiteTime ?? undefined,
                dispatchingIntervalTime: formIntervalTime ?? undefined,
            }]
            : [];
    }, [orderLineItemDto, formYardTime, formOnsiteTime, formIntervalTime]);

    return (
        <Dialog open={open} maxWidth='md' fullWidth>
            <DialogTitle sx={{ backgroundColor: 'primary', color: '#ffffff' }}>
                <Grid container direction='row' alignItems='center' gap={1}>
                    Edit Order Line Item
                </Grid>
            </DialogTitle>
            <DialogContent>
                <Grid container direction='column' mt={1} spacing={1}>
                    <Grid item container direction='row' spacing={1}>
                        <Grid item xs={6}>
                            <TimePicker
                                label='Yard Time'
                                value={formYardTime}
                                onChange={(date) => {
                                    if (date) {
                                        if (!isNaN(Date.parse(date.toString()))) {
                                            var adjustedDate = adjustSecondDateToMatchFirst(orderLineItemDto?.orderDate, date);
                                            setFormYardTime(adjustedDate);
                                            setTimesChanged(true);
                                        }
                                        else {
                                            setFormYardTime(null);
                                        }
                                    }
                                }}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TimePicker
                                label='Onsite Time'
                                value={formOnsiteTime}
                                onChange={(date) => {
                                    if (date) {
                                        if (!isNaN(Date.parse(date.toString()))) {
                                            var adjustedDate = adjustSecondDateToMatchFirst(orderLineItemDto?.orderDate, date);
                                            setFormOnsiteTime(adjustedDate);
                                            setTimesChanged(true);
                                        }
                                        else {
                                            setFormOnsiteTime(null);
                                        }
                                    }
                                }}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TimeIntervalPicker
                                label={`Interval Time`}
                                value={formIntervalTime}
                                onChange={(interval) => {
                                    setFormIntervalTime(interval);
                                    setTimesChanged(true);
                                }}
                                disabled={disabled}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant='h6'>Memos</Typography>
                    </Grid>
                    <MemoForm
                        quoteMemo={orderLineItemDto?.quoteMemo}
                        dispatchBillingMemo={formDispatchBillingMemo}
                        lineItemMemo={orderLineItemDto?.lineItemMemo}
                        orderLineItemDriverMemo={formDriverMemo}
                        handleDispatchBillingMemoChange={setFormDispatchBillingMemo}
                        handleOrderLineItemDriverMemoChange={setFormDriverMemo}
                        hideQuoteMemo={true}
                        hideLineItemMemo={true}
                        hideDriverComment={true}
                        disabled={disabled}
                    />
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' disabled={!isFormDirty || disabled} onClick={handleSaveClicked}>
                    SAVE
                </Button>
                <Button variant='outlined' onClick={handleCancel}>
                    CANCEL
                </Button>
            </DialogActions>
            {showAffectedDispatchesDialog && affectedDispatches && affectedDispatches.length > 0 &&
                <AffectedDispatchesDialog
                    open={showAffectedDispatchesDialog}
                    orderLineItems={memoizedOrderLineItems}
                    onSuccess={handleAffectedDispatchesDialogSuccess}
                    onCancel={handleAffectedDispatchesDialogCancel}
                    isLoading={isGettingAffectedDispatches}
                    affectedDispatches={affectedDispatches}
                />
            }
        </Dialog>
    );
}